import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { api, formatDateTime } from '../../utils/api'
import '../../styles/pages.css'

function VariantDetail() {
  const { id } = useParams()
  const navigate = useNavigate()

  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(null)

  const [variant, setVariant] = useState(null)

  // Form state for dimensions
  const [dimensions, setDimensions] = useState([])
  const [isDirty, setIsDirty] = useState(false)

  useEffect(() => {
    loadVariant()
  }, [id])

  async function loadVariant() {
    setLoading(true)
    setError(null)
    try {
      const response = await api.get(`/api/pim/variants/${id}`)
      setVariant(response)
      setDimensions(response.dimensions?.map(d => ({
        dimension_type_id: d.dimension_type_id,
        dimension_type_name: d.dimension_type_name,
        value: d.value
      })) || [])
      setIsDirty(false)
    } catch (err) {
      console.error('Failed to load variant:', err)
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  function handleDimensionChange(index, value) {
    setDimensions(prev => {
      const updated = [...prev]
      updated[index] = { ...updated[index], value }
      return updated
    })
    setIsDirty(true)
    setSuccess(null)
  }

  async function handleSave(e) {
    e.preventDefault()
    setSaving(true)
    setError(null)
    setSuccess(null)

    try {
      const updates = {
        dimensions: dimensions.map(d => ({
          dimension_type_id: d.dimension_type_id,
          value: d.value
        }))
      }

      const response = await api.patch(`/api/pim/variants/${id}`, updates)
      setVariant(response)
      setDimensions(response.dimensions?.map(d => ({
        dimension_type_id: d.dimension_type_id,
        dimension_type_name: d.dimension_type_name,
        value: d.value
      })) || [])
      setIsDirty(false)
      setSuccess('Variant saved successfully')
    } catch (err) {
      console.error('Failed to save variant:', err)
      setError(err.message)
    } finally {
      setSaving(false)
    }
  }

  function handleBackToProduct() {
    if (variant?.master_product_id) {
      navigate(`/pim/products/${variant.master_product_id}`)
    } else {
      navigate('/pim/products')
    }
  }

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--space-12)' }}>
        <div className="loading-spinner" />
      </div>
    )
  }

  if (!variant) {
    return (
      <div className="card">
        <div className="empty-state">
          <div className="empty-state-title">Variant Not Found</div>
          <div className="empty-state-description">The requested variant could not be found.</div>
          <button className="btn btn-primary" onClick={() => navigate('/pim/products')}>
            Back to Products
          </button>
        </div>
      </div>
    )
  }

  return (
    <>
      {/* Back button */}
      <button
        type="button"
        className="btn btn-secondary"
        onClick={handleBackToProduct}
        style={{ marginBottom: 'var(--space-4)' }}
      >
        &larr; Back to {variant.master_product_model}
      </button>

      {error && (
        <div className="card" style={{ background: 'var(--color-error-light)', padding: 'var(--space-4)', marginBottom: 'var(--space-4)' }}>
          <strong>Error:</strong> {error}
        </div>
      )}

      {success && (
        <div className="card" style={{ background: 'var(--color-success-light)', padding: 'var(--space-4)', marginBottom: 'var(--space-4)' }}>
          {success}
        </div>
      )}

      {/* Variant Header */}
      <div className="card" style={{ marginBottom: 'var(--space-4)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <h2 style={{ marginBottom: 'var(--space-2)' }}>
              {variant.brand?.name} {variant.master_product_model}
            </h2>
            <div className="text-muted">
              {variant.sku ? (
                <span style={{ fontFamily: 'monospace' }}>SKU: {variant.sku}</span>
              ) : (
                <span>No SKU assigned</span>
              )}
            </div>
          </div>
          <div className="text-sm text-muted text-right">
            <div>Created: {formatDateTime(variant.created_at)}</div>
            <div>Updated: {formatDateTime(variant.updated_at)}</div>
          </div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 300px', gap: 'var(--space-4)' }}>
        {/* Images */}
        <div className="card">
          <h3 style={{ marginBottom: 'var(--space-4)' }}>
            Images ({variant.image_urls?.length || 0})
          </h3>

          {variant.image_urls?.length === 0 ? (
            <div className="empty-state">
              <div className="empty-state-description">No images available for this variant.</div>
            </div>
          ) : (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
              gap: 'var(--space-3)'
            }}>
              {variant.image_urls?.map((url, index) => (
                <div
                  key={index}
                  style={{
                    aspectRatio: '1',
                    borderRadius: 8,
                    overflow: 'hidden',
                    background: 'var(--color-bg-secondary)'
                  }}
                >
                  <img
                    src={url}
                    alt={`${variant.master_product_model} - Image ${index + 1}`}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover'
                    }}
                  />
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Dimensions */}
        <div className="card">
          <h3 style={{ marginBottom: 'var(--space-4)' }}>Dimensions</h3>

          {dimensions.length === 0 ? (
            <div className="empty-state">
              <div className="empty-state-description">No dimensions defined for this variant.</div>
            </div>
          ) : (
            <form onSubmit={handleSave}>
              {dimensions.map((dim, index) => (
                <div key={dim.dimension_type_id} className="filter-group" style={{ marginBottom: 'var(--space-3)' }}>
                  <label className="filter-label">{dim.dimension_type_name}</label>
                  <input
                    type="text"
                    className="form-input"
                    value={dim.value}
                    onChange={e => handleDimensionChange(index, e.target.value)}
                  />
                </div>
              ))}

              <button
                type="submit"
                className="btn btn-primary"
                style={{ width: '100%', marginTop: 'var(--space-4)' }}
                disabled={!isDirty || saving}
              >
                {saving ? 'Saving...' : 'Save Dimensions'}
              </button>
            </form>
          )}
        </div>
      </div>
    </>
  )
}

export default VariantDetail
